import { MenuIcon } from "@cmp-src/icons/TacticIcons/MenuIcons";
import { cn } from "@gettactic/helpers";
import { Button, ButtonProps } from "@cmp-src/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@cmp-src/primitives/dropdown-menu";

import { ISchedule } from "@gettactic/api";
import { JoinButton } from "../JoinButton/JoinButton";

const borderClass = "border-1";

const hoverClass = `hover:${borderClass} hover:border-neutral-500 hover:shadow-md`;

// adjust top div className when changing this
const cardRounded = "xl";

// reason for this spacings is to accommodate the rainbow border
// those got to be same size, but in separate strings
const pbToCompensateButton = "pb-[3px]";
const mRainbow = "md:m-[3px]";
const mxRainbow = "md:mx-[3px]";
export const pxRainbow = "px-[3px]";
// <-- those got to be same size

const maxLeftBottomWidth = {
  withRight: 250,
  withoutRight: 330
};

export interface AgendaCardLayoutProps {
  topLeft?: React.ReactNode;
  topRight?: React.ReactNode;
  bottomLeft?: React.ReactNode;
  bottomRight?: React.ReactNode;
  button?: React.ReactNode;
  onCardClick?: () => void;
  dotsMenu?: React.ReactNode;
  isSelected?: boolean;
  hasHover?: boolean;
  className?: string;
  innerClassName?: string;
}

export const AgendaCardLayout = ({
  topLeft,
  topRight,
  bottomLeft,
  bottomRight,
  button,
  onCardClick,
  dotsMenu,
  hasHover,
  isSelected,
  className,
  innerClassName
}: AgendaCardLayoutProps) => {
  return (
    <div
      className={cn(
        "group/card flex flex-col",
        "rounded-2xl",
        !!button && pbToCompensateButton,
        className
      )}
      style={
        isSelected
          ? {
              backgroundImage: "var(--rainbow)"
            }
          : {}
      }
    >
      <div
        onClick={onCardClick}
        className={cn(
          mRainbow,
          `z-10 cursor-pointer ${borderClass} border-transparent bg-background text-sm`,
          "p-3",
          `rounded-${cardRounded}`,
          hasHover && !isSelected && hoverClass,
          isSelected ? "border-transparent" : "border-neutral-200",
          innerClassName
        )}
      >
        <div className="flex justify-between">
          {!!(topLeft || topRight) && (
            <div className="flex items-center justify-between">
              {topLeft}
              {topRight}
            </div>
          )}
          {dotsMenu}
        </div>
        {!!(bottomLeft || bottomRight) && (
          <div className="mt-4 flex justify-between">
            <div
              className="flex flex-col items-start"
              style={{
                maxWidth: bottomRight
                  ? maxLeftBottomWidth.withRight
                  : maxLeftBottomWidth.withoutRight
              }}
            >
              {bottomLeft}
            </div>
            {bottomRight}
          </div>
        )}
      </div>
      {!!button && <div className={`-mt-14 pt-8 ${mxRainbow}`}>{button}</div>}
    </div>
  );
};

export interface DotsMenuProps {
  onlineMeeting?: ISchedule["online_meeting"] | null;
  onRemove?: (e?: React.MouseEvent<HTMLElement>) => void;
  onManage?: (e?: React.MouseEvent<HTMLElement>) => void;
  onDuplicate?: (e?: React.MouseEvent<HTMLElement>) => void;
  onExtend?: (e?: React.MouseEvent<HTMLElement>) => void;
  canRemove?: boolean;
  canManage?: boolean;
  canDuplicate?: boolean;
  canExtend?: boolean;
  onRefetch?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  triggerClassName?: string;
}

export const DotsMenu = ({
  onRemove,
  onManage,
  canRemove = true,
  canManage = true,
  onDuplicate,
  onExtend,
  onlineMeeting,
  onRefetch,
  triggerClassName,
  canDuplicate = true,
  canExtend = false
}: DotsMenuProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="outline-none" data-dots-menu="true" type="button">
          <span
            className={cn(
              "block px-2 text-muted-foreground hover:text-primary cursor-pointer",
              triggerClassName
            )}
          >
            <MenuIcon />
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <JoinButton
          onlineMeeting={onlineMeeting}
          iconOnly={false}
          onRefetch={onRefetch}
          wrapper={(children, icon, props) => (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                if (props.onClick) props.onClick(e);
              }}
              disabled={props.disabled}
            >
              {icon}
              {children}
            </DropdownMenuItem>
          )}
        />
        {canManage && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              if (onManage) onManage(e);
            }}
          >
            Manage
          </DropdownMenuItem>
        )}
        {canExtend && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              if (onExtend) onExtend(e);
            }}
          >
            Extend
          </DropdownMenuItem>
        )}
        {canDuplicate && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              if (onDuplicate) onDuplicate(e);
            }}
          >
            Duplicate
          </DropdownMenuItem>
        )}
        {canRemove && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              if (onRemove) onRemove(e);
            }}
            variant="danger"
          >
            Remove
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const AgendaCardButton = (props: ButtonProps) => {
  return (
    <Button
      className={cn(
        "h-16 w-full pt-7",
        `rounded-${cardRounded}`,
        props.className
      )}
      {...props}
    />
  );
};
